/**
 * Runs NativeBridge calls relayed from the chat iframe (chat-framework), which
 * runs cross-origin inside this claims WebView. On iOS the native shell injects
 * `window.NativeBridge` into the top WebView only (WKUserScript defaults to
 * `forMainFrameOnly`), so the iframe can't call the bridge itself — a cashless
 * letter link or the cult home deeplink silently no-ops. The iframe posts an
 * NB_CALLBACK message and this parent (top WebView, has the bridge on both
 * platforms) runs it.
 *
 * This is a privileged path, so it is deliberately narrow: only `callDeeplink`
 * (the sole method chat-framework can send) and only to a `deeplink.plumhq.com`
 * URL. Anything else — a wider method, a non-deeplink target (e.g. a prompt-
 * injected markdown link reaching InterceptedLink) — is rejected and logged.
 */

// The only method chat-framework's relay can emit (mirrors TNativeBridgeMethod).
const RELAYABLE_METHODS = ['callDeeplink'] as const;

// Relayed callDeeplink args are always the app deeplink host (built from
// APP_DEEPLINK_WEBOUTSIDE_URL, or the server-wrapped letter/cult deeplinks).
const ALLOWED_DEEPLINK_PREFIX = 'https://deeplink.plumhq.com/';

type TRelayableMethod = (typeof RELAYABLE_METHODS)[number];

const isRelayableMethod = (value: unknown): value is TRelayableMethod =>
  typeof value === 'string' &&
  (RELAYABLE_METHODS as readonly string[]).includes(value);

/**
 * The NB_CALLBACK relay drives the privileged native bridge, so it must accept
 * messages only from the **active bot iframe** — not some other frame or popup
 * that happens to sit on an allowed origin. Fails closed when the source is
 * unknown (origin is already validated separately by the listener).
 */
export const isTrustedRelaySource = (
  source: MessageEventSource | null,
  iframeWindow: Window | null | undefined,
): boolean => source != null && source === iframeWindow;

/**
 * Runs a NativeBridge call relayed from the chat iframe (the `data` of an
 * NB_CALLBACK message). The message origin and source are validated by the
 * caller before this runs. Rejections are logged so the privileged path stays
 * debuggable (the whole point of the relay is diagnosing "link does nothing").
 */
export const runRelayedNativeBridgeCall = (data: unknown) => {
  const { method, args } = (data ?? {}) as {
    method?: unknown;
    args?: unknown;
  };
  if (!isRelayableMethod(method)) {
    window.dev?.i?.('nativeBridgeRelay: method not relayable, ignored', method);
    return;
  }
  const bridgeArgs = Array.isArray(args) ? (args as Array<string>) : [];
  const url = bridgeArgs[0];
  if (typeof url !== 'string' || !url.startsWith(ALLOWED_DEEPLINK_PREFIX)) {
    window.dev?.i?.('nativeBridgeRelay: deeplink target rejected', url);
    return;
  }
  (
    window?.NativeBridge as unknown as Record<
      string,
      ((...a: Array<string>) => void) | undefined
    >
  )?.[method]?.(...bridgeArgs);
};
